import { useCallback } from 'react';

export enum ImageModalV2Mode {
    VIEW = 'VIEW',
    CROP = 'CROP',
    BACKGROUND = 'BACKGROUND',
    DRAW = 'DRAW',
    UPLOAD = 'UPLOAD',
    DOWNLOAD = 'DOWNLOAD',
}

export const isImageModalV2ZoomEnabled = (mode: ImageModalV2Mode): boolean =>
    mode === ImageModalV2Mode.VIEW || mode === ImageModalV2Mode.UPLOAD || mode === ImageModalV2Mode.DOWNLOAD;

export const shouldShowImageModalV2DrawingOverlay = (mode: ImageModalV2Mode): boolean => mode !== ImageModalV2Mode.DRAW;

export const shouldShowImageModalV2TransparencyGrid = (_mode: ImageModalV2Mode): boolean => true;

/** Keeps the view image painted until the image-producing editor reports its first ready frame. */
export const shouldHideImageModalV2ViewForEditor = (mode: ImageModalV2Mode, editorIsReady: boolean): boolean =>
    editorIsReady && (mode === ImageModalV2Mode.CROP || mode === ImageModalV2Mode.BACKGROUND);

type UseImageModalV2ModeProps = {
    isEditable: boolean;
    isEditing: boolean;
    isEditingBackground: boolean;
    isEditingDrawing: boolean;
    isUploadingAttachment: boolean;
    isDownloadingAttachment: boolean;
    setRouteMode: (mode: ImageModalV2Mode) => void;
};

type UseImageModalV2ModeReturn = {
    mode: ImageModalV2Mode;
    setMode: (mode: ImageModalV2Mode) => void;
    isToolPanelOpen: boolean;
};

const getImageModalV2Mode = ({
    isEditable,
    isEditing,
    isEditingBackground,
    isEditingDrawing,
    isUploadingAttachment,
    isDownloadingAttachment,
}: UseImageModalV2ModeProps) => {
    if (!isEditable) return ImageModalV2Mode.VIEW;
    if (isEditing) return ImageModalV2Mode.CROP;
    if (isEditingBackground) return ImageModalV2Mode.BACKGROUND;
    if (isEditingDrawing) return ImageModalV2Mode.DRAW;
    if (isUploadingAttachment) return ImageModalV2Mode.UPLOAD;
    if (isDownloadingAttachment) return ImageModalV2Mode.DOWNLOAD;

    return ImageModalV2Mode.VIEW;
};

export const useImageModalV2Mode = (props: UseImageModalV2ModeProps): UseImageModalV2ModeReturn => {
    const { isEditable, setRouteMode } = props;
    const mode = getImageModalV2Mode(props);

    const setMode = useCallback(
        (newMode: ImageModalV2Mode) => {
            if (!isEditable) return;

            setRouteMode(newMode);
        },
        [isEditable, setRouteMode],
    );

    const isToolPanelOpen =
        mode === ImageModalV2Mode.CROP || mode === ImageModalV2Mode.BACKGROUND || mode === ImageModalV2Mode.DRAW;

    return { mode, setMode, isToolPanelOpen };
};
